{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2019 - 2020                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Utils;

interface

uses
  Types, Web, SysUtils, Math, Classes, JS;

const
  DayMonday = 1;
  DayTuesday = 2;
  DayWednesday = 3;
  DayThursday = 4;
  DayFriday = 5;
  DaySaturday = 6;
  DaySunday = 7;

  MonthJanuary = 1;
  MonthFebruary = 2;
  MonthMarch = 3;
  MonthApril = 4;
  MonthMay = 5;
  MonthJune = 6;
  MonthJuly = 7;
  MonthAugust = 8;
  MonthSeptember = 9;
  MonthOctober = 10;
  MonthNovember = 11;
  MonthDecember = 12;

type
  TInterfaceList = class;

  Int64 = NativeInt {unimplemented}; // only 53 bits at runtime

  TProc = reference to procedure;
  TProcBoolean = reference to procedure(AValue: Boolean);
  TProcInteger = reference to procedure(AValue: Integer);
  TProcString = reference to procedure(AValue: string);

  EConvertError = class(Exception);

  EEncodingError = class(Exception);

  EStreamError = class(Exception);
  EStringStreamError = class(EStreamError);
  EFilerError = class(EStreamError);
  EReadError = class(EFilerError);
  EWriteError = class(EFilerError);

  TBytesHelper = record helper for TBytes
  strict private
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    function Compare(const ARight: TBytes): Integer;
    function High: Integer;
    function Low: Integer;
    function SubBytes(const AStartIndex: Integer): TBytes; overload; inline;
    function SubBytes(const AStartIndex, ALength: Integer): TBytes; overload; inline;
    property Length: Integer read get_Length write set_Length;
  end;

  TWords = array of Word;
  TWordsHelper = record helper for TWords
  strict private
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    class function FromBytes(const ABytes: TBytes): TWords; static;
    function High: Integer;
    function Low: Integer;
    function ToBytes: TBytes;
    property Length: Integer read get_Length write set_Length;
  end;

  TCharHelper = record helper for Char
  public const
    Length = 1;
    Size = 2;
  public
    function ToOrd: Integer;
    function ToUpper: Char; overload;
    class function ToUpper(const AValue: Char): Char; overload; assembler; static;
  end;

  TIntegerHelper = record helper for Integer
  public
    function Equals(const AValue: Integer): Boolean; inline;
    function NotEquals(const AValue: Integer): Boolean; inline;
    class function Parse(const AValue: string): Integer; static;
    function ToString: string; overload;
    class function ToString(const AValue: Integer): string; overload; assembler; static;
    class function TryParse(const AString: string; out AValue: Integer): Boolean; static; inline;
  end;

  TLongIntHelper = record helper for LongInt
  public
    function Equals(const AValue: LongInt): Boolean; inline;
    function NotEquals(const AValue: LongInt): Boolean; inline;
    class function Parse(const AValue: string): Integer; static;
    function ToString: string; overload;
    class function ToString(const AValue: LongInt): string; overload; assembler; static;
    class function TryParse(const AString: string; out AValue: Integer): Boolean; static; inline;
  end;

  TWordHelper = record helper for Word
  public
    class function FromBytes(const ALow, AHigh: Byte): Word; static;
    procedure ToBytes(out ALow, AHigh: Byte);
  end;

  TStringHelper = record helper for string
  strict private
    function get_Chars(const AIndex: Integer): Char; inline;
    function get_Length: Integer; inline;
    function Pos(const ASubStr, AStr: string; const AOffset: Integer): Integer;
    function Reverse: string;
    procedure set_Chars(const AIndex: Integer; const AValue: Char); inline;
    procedure set_Length(const AValue: Integer); inline;
  public const
    Empty = '';
  public
    function CompareTo(const AValue: string): Integer; overload;
    class function CompareTo(const ALeft, ARight: string): Integer; overload; assembler; static;
    function Contains(const AValue: string): Boolean;
    function Equals(const AValue: string): Boolean;
    function FirstChar: Char;
    function Format(const AArgs: array of JSValue): string; overload;
    class function Format(const AValue: string; const AArgs: array of JSValue): string; overload; static;
    function High: Integer; overload; inline;
    function IndexOf(const AValue: Char): Integer; overload; inline;
    function IndexOf(const AValue: string; const AStartIndex, ACount: Integer): Integer; overload;
    function IndexOf(const AValue: string): Integer; overload; inline;
    function Insert(const AStartIndex: Integer; const AValue: string): string;
    function IsEmpty: Boolean; inline;
    function IsNumber: Boolean;
    function LastIndexOf(const AValue: Char): Integer; overload; inline;
    class function Low: Integer; static; inline;
    function NotEquals(const AValue: string): Boolean;
    function NotIsEmpty: Boolean; inline;
    function PadLeft(const ATotalWidth: Integer): string; overload; inline;
    function PadLeft(ATotalWidth: Integer; const APaddingChar: Char): string; overload; inline;
    function Remove(const AStartIndex: Integer): string; overload; inline;
    function Remove(const AStartIndex, ACount: Integer): string; overload; inline;
    function Replace(const AOldValue: string; const ANewValue: string): string; overload;
    function Substring(const AStartIndex: Integer): string; overload; inline;
    function Substring(const AStartIndex, ALength: Integer): string; overload; inline;
    function ToLower: string;
    function ToUpper: string;
    property Chars[const AIndex: Integer]: Char read get_Chars write set_Chars;
    property Length: Integer read get_Length write set_Length;
  end;

  TDateTimeHelper = record helper for TDateTime
  public
    function Equals(const AValue: TDateTime): Boolean; inline;
    function NotEquals(const AValue: TDateTime): Boolean; inline;
    function ToString: string; inline;
  end;

  TMonthNameArray = array[1..12] of string;
  TMonthNames = TMonthNameArray;
  TDayNames = array[1..7] of string;
  TFormatSettings = record
  strict private
    class function GetBrowserLocale: string; assembler; static;
    class function GetLocaleShortDayName(const ADayOfWeek: Integer; const ALocale: string): string; assembler; static;
    class function GetLocaleDecimalSeparator(const ALocale: string): string; assembler; static;
    class function GetLocaleLongMonthName(const AMonth: Integer; const ALocale: string): string; assembler; static;
    class function GetLocaleLongDayName(const ADayOfWeek: Integer; const ALocale: string): string; assembler; static;
    class function GetLocaleShortMonthName(const AMonth: Integer; const ALocale: string): string; assembler; static;
    class function GetLocaleShortDateFormat(ALocale: string): string; static;
  public
    CurrencyDecimals: Byte;
    CurrencyFormat: Byte;
    CurrencyString: string;
    DateSeparator: Char;
    DateTimeToStrFormat: array[Boolean] of string;
    DecimalSeparator: string;
    LongDateFormat: string;
    LongDayNames: TDayNames;
    LongMonthNames: TMonthNames;
    LongTimeFormat: string;
    NegCurrFormat: Byte;
    ShortDateFormat: string;
    ShortDayNames: TDayNames;
    ShortMonthNames: TMonthNames;
    ShortTimeFormat: string;
    ThousandSeparator: string;
    TimeAMString: string;
    TimePMString: string;
    TimeSeparator: Char;
    TwoDigitYearCenturyWindow: Word;
  public
    class function Create: TFormatSettings; overload; static;
    class function Create(const ALocale: string): TFormatSettings; overload; static;
  end;

  TEncoding = class(TObject)
  strict private class var
    FANSI: TEncoding;
    FUnicode: TEncoding;
  strict private
    class function get_ANSI: TEncoding; static;
    class function get_Default: TEncoding; static;
    class function get_Unicode: TEncoding; static;
  public
    function GetByteCount(const AChars: string): Integer; overload; virtual; abstract;
    function GetByteCount(const AChars: string; const ACharCount: Integer): Integer; overload;
    function GetByteCount(const AChars: string; const ACharIndex, ACharCount: Integer): Integer; overload; inline;
    function GetBytes(const AChars: string): TBytes; overload; virtual; abstract;
    function GetBytes(const AChars: string; const ACharCount: Integer): TBytes; overload;
    function GetBytes(const AChars: string; const ACharIndex, ACharCount: Integer): TBytes; overload; inline;
    function GetString(const ABytes: TBytes): string; overload; virtual; abstract;
    function GetString(const ABytes: TBytes; const ACharCount: Integer): string; overload;
    function GetString(const ABytes: TBytes; const ACharIndex, ACharCount: Integer): string; overload; inline;
    class function IsStandardEncoding(AEncoding: TEncoding): Boolean; static;
    class property ANSI: TEncoding read get_ANSI;
    class property Default: TEncoding read get_Default;
    class property Unicode: TEncoding read get_Unicode;
  end;

  TANSIEncoding = class(TEncoding)
  public
    function GetByteCount(const AChars: string): Integer; overload; override;
    function GetBytes(const AChars: string): TBytes; overload; override;
    function GetString(const ABytes: TBytes): string; overload; override;
  end;

  TUnicodeEncoding = class(TEncoding)
  strict private
    procedure InternalGetBytes(const AChars: string; out ABytes: TBytes);
  public
    function GetByteCount(const AChars: string): Integer; overload; override;
    function GetBytes(const AChars: string): TBytes; overload; override;
    function GetString(const ABytes: TBytes): string; overload; override;
  end;

  TStream = class(TObject)
  private
    FPosition: Int64;
    FSize: Int64;
  protected
    procedure set_Size(const ANewSize: Int64); virtual;
  public
    function CopyFrom(const ASource: TStream; ACount: Int64): Int64;
    function Read(var ABuffer: TBytes; const ACount: Longint): Longint; overload;
    function Read(var ABuffer: TBytes; const AOffset, ACount: Longint): Longint; overload; virtual;
    procedure ReadBuffer(var ABuffer: TBytes; const ACount: NativeInt); overload;
    procedure ReadBuffer(var Buffer: TBytes; const AOffset, ACount: NativeInt); overload;
    function Write(const ABuffer: TBytes; const ACount: Longint): Longint; overload; virtual;
    function Write(const ABuffer: TBytes; const AOffset, ACount: Longint): Longint; overload;
    procedure WriteBuffer(const ABuffer: TBytes; const ACount: NativeInt); overload;
    procedure WriteBuffer(const Buffer: TBytes; Offset, Count: NativeInt); overload;
    property Position: Int64 read FPosition write FPosition;
    property Size: Int64 read FSize write set_Size;
  end;

  TCustomMemoryStream = class(TStream)
  private
    FBytes: TBytes;
    FCapacity: NativeInt;
    procedure set_Capacity(const ANewCapacity: NativeInt);
  protected
    procedure set_Size(const ANewSize: Int64); override;
    property Capacity: NativeInt read FCapacity write set_Capacity;
  public
    procedure Clear;
    procedure LoadFromStream(const AStream: TStream);
    function Read(var ABuffer: TBytes; const AOffset, ACount: Longint): Longint; overload; override;
    procedure SaveToStream(const AStream: TStream);
    function Write(const ABuffer: TBytes; const ACount: Longint): Longint; overload; override;
    property Bytes: TBytes read FBytes;
  end;

  TMemoryStream = class(TCustomMemoryStream)
  end;

  TBytesStream = class(TMemoryStream)
  end;

  TStringStream = class(TBytesStream)
  private
    FEncoding: TEncoding;
    FFileName: string;
    FOwnsEncoding: Boolean;
    FError: TProcString;
    FLoaded: TProc;
    FRequest: TJSXMLHTTPRequest;
    function GetDataString: string;
    function OnAbort(AEvent: TEventListenerEvent): Boolean;
    function OnError(AEvent: TEventListenerEvent): Boolean;
    function OnLoad(AEvent: TEventListenerEvent): Boolean;
  public
    constructor Create; overload;
    constructor Create(const AString: string); overload;
    constructor Create(const AString: string; AEncoding: TEncoding; AOwnsEncoding: Boolean = True); overload;
    destructor Destroy; override;
    procedure LoadFromFile(const AFileName: string); overload;
    procedure LoadFromFile(const AFileName: string; const ALoaded: TProc); overload;
    procedure LoadFromFile(const AFileName: string; const ALoaded: TProc; const AError: TProcString); overload;
    function ReadString(ACount: Integer): string;
    procedure WriteString(const AString: string);
    property DataString: string read GetDataString;
  end;

  IInterfaceList = interface
  ['{285DEA8A-B865-11D1-AAA7-00C04FB17A72}']
    function Add(const AItem: IInterface): Integer;
    procedure Clear;
    procedure Delete(const AIndex: Integer);
    procedure Exchange(const AIndex1, AIndex2: Integer);
    function Expand: TInterfaceList;
    function First: IInterface;
    function get_Capacity: Integer;
    function get_Count: Integer;
    function get_Items(const AIndex: Integer): IInterface;
    function IndexOf(const AItem: IInterface): Integer;
    function IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure Insert(const AIndex: Integer; const AItem: IInterface);
    function Last: IInterface;
    procedure Lock;
    function Remove(const AItem: IInterface): Integer;
    function RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure set_Capacity(const AValue: Integer);
    procedure set_Count(const AValue: Integer);
    procedure set_Items(const AIndex: Integer; const AItem: IInterface);
    procedure Unlock;
    property Count: Integer read get_Count write set_Count;
    property Items[const AIndex: Integer]: IInterface read get_Items write set_Items; default;
  end;

  TInterfaceListEnumerator = class(TObject)
  strict private
    FIndex: Integer;
    FInterfaceList: TInterfaceList;
    function get_Current: IInterface; inline;
  public
    constructor Create(const AInterfaceList: TInterfaceList);
    function MoveNext: Boolean;
    property Current: IInterface read get_Current;
  end;

  IInterfaceListEx = interface(IInterfaceList)
    ['{FDB39D70-65B9-4995-9436-6084ACA05DB3}']
    function GetEnumerator: TInterfaceListEnumerator;
  end;

  TCompareItems = reference to function(const ALeft, ARight: IInterface): TValueRelationship;

  IInterfaceListEx2 = interface(IInterfaceList)
    ['{4FE71218-0F8F-4027-B050-5E84E2202604}']
    procedure Sort(const ACompare: TCompareItems);
  end;

  TInterfaceList = class(TInterfacedObject, IInterfaceList, IInterfaceListEx, IInterfaceListEx2)
  strict private
    FList: TFPList;
  strict protected
    function Add(const AItem: IInterface): Integer;
    procedure Clear;
    procedure Delete(const AIndex: Integer);
    procedure Exchange(const AIndex1, AIndex2: Integer);
    function Expand: TInterfaceList;
    function First: IInterface;
    function GetEnumerator: TInterfaceListEnumerator;
    function get_Capacity: Integer;
    function get_Count: Integer;
    function get_Items(const AIndex: Integer): IInterface;
    function IndexOf(const AItem: IInterface): Integer;
    function IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure Insert(const AIndex: Integer; const AItem: IInterface);
    function Last: IInterface;
    procedure Lock;
    function Remove(const AItem: IInterface): Integer;
    function RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure set_Capacity(const AValue: Integer);
    procedure set_Count(const AValue: Integer);
    procedure set_Items(const AIndex: Integer; const AItem: IInterface);
    procedure Sort(const ACompare: TCompareItems);
    procedure Unlock;
    property Capacity: Integer read get_Capacity write set_Capacity;
    property Items[const AIndex: Integer]: IInterface read get_Items write set_Items; default;
  protected
    property Count: Integer read get_Count write set_Count;
  public
    constructor Create;
    destructor Destroy; override;
  end;

  TPath = record
  public
    class function Combine(const APath1, APath2: string): string; static;
    class function GetDirectoryName(const AFileName: string): string; static;
  end;

  TDirectory = record
  public
    class function GetCurrentDirectory: string; static;
  end;

  TStringBuilder = class(TObject)
  strict private
    FData: string;
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    constructor Create(const AValue: string); overload;
    function Append(const AValue: Integer): TStringBuilder; overload;
    function Append(const AValue: string): TStringBuilder; overload;
    function Insert(const AIndex: Integer; const AValue: Integer): TStringBuilder; overload;
    function Insert(const AIndex: Integer; const AValue: string): TStringBuilder; overload;
    function ToString: string; overload; override;
    property Length: Integer read get_Length write set_Length;
  end;

function DateTimeToStr(const ADateTime: TDateTime; const AFormatSettings: TFormatSettings; const AForceTimeIfZero: Boolean = False): string;
function DateToStr(const ADateTime: TDateTime; const AFormatSettings: TFormatSettings): string;
function FormatDateTime(const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime): string;

function TryStrToDate(const AString: string; out AValue: TDateTime; const AFormatSettings: TFormatSettings): Boolean; overload;

procedure UseVariable(const AValue: IInterface); overload;
procedure UseVariable(const AValue: Integer); overload;
procedure UseVariable(const AValue: string); overload;

var
  FormatSettings: TFormatSettings;

implementation

uses
  RTLConsts, WEBLib.Consts;

var
  FCompare: TCompareItems;

type
  TFormatDateTimeHelper = record
  strict private
    class procedure StoreFormat(var AResult: string; var AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek: Word; const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime; ANesting: Integer; ATimeFlag: Boolean); static;
    class procedure StoreInt(var AResult: string; const AValue, ADigits: Integer); static;
    class procedure StoreStr(var AResult: string; const AFormatStr: string; const APos, ALength: Integer); static;
    class procedure StoreString(var AResult: string; const AString: string); static;
  public
    class function FormatDateTime(const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime): string; static;
  end;

function DateTimeToStr(const ADateTime: TDateTime; const AFormatSettings: TFormatSettings; const AForceTimeIfZero: Boolean = False): string;
begin
  Result := FormatDateTime(AFormatSettings.DateTimeToStrFormat[AForceTimeIfZero], AFormatSettings, ADateTime);
end;

function DateToStr(const ADateTime: TDateTime; const AFormatSettings: TFormatSettings): string;
const
  cDateFormat = 'ddddd';
begin
  Result := FormatDateTime(cDateFormat, AFormatSettings, ADateTime);
end;

function FormatDateTime(const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime): string;
begin
  Result := TFormatDateTimeHelper.FormatDateTime(AFormatStr, AFormatSettings, ADateTime);
end;

function IntStrToDate(out AErrorMsg : string; const AString: string; const AUseFormat: string; const AFormatSettings: TFormatSettings; ASeparator: Char): TDateTime;
const
  cWhiteSpace = ' '#8#9#10#12#13;
  cDigits = '0123456789';

  procedure FixErrorMsg(const AErrMarg : string);
  begin
    AErrorMsg := Format(SInvalidDateFormat, [AErrMarg]);
  end;

var
  lCode: Integer;
  lCount: Longint;
  lDateFormat: string;
  lDay: Word;
  lDayPos: Byte;
  lDayValues: Word;
  lLen: Longint;
  lMonth: Word;
  lMonthPos: Byte;
  lMonthValues: Word;
  lNumSep: Longint;
  lS1: string;
  lValues: array of Integer;
  lWhich: Byte;
  lYear: Word;
  lYearMoreThenTwoDigits: Boolean;
  lYearPos: Byte;
  lYearValues: Word;
begin
  SetLength(lValues, 4);
  Result := 0;
  lLen := Length(AString);
  AErrorMsg := '';
  while (lLen > 0) and (Pos(AString[lLen], cWhiteSpace) > 0) do
    Dec(lLen);

  if lLen = 0 then
  begin
    FixErrorMsg(AString);
    Exit;
  end;

  lYearMoreThenTwoDigits := False;
  if ASeparator = #0 then
  begin
    if AFormatSettings.DateSeparator <> #0 then
      ASeparator := AFormatSettings.DateSeparator
    else
      ASeparator := '-';
  end;
  // Writeln('Separator: ',ASeparator);

  lDateFormat := UpperCase(AUseFormat);
  { Determine order of lDayValues,lMonthValues,lYearValues }
  lYearPos := 0;
  lMonthPos := 0;
  lDayPos := 0;
  lWhich := 0;
  lCount := 0;
  while (lCount < Length(lDateFormat)) and (lWhich < 3) do
  begin
    Inc(lCount);
    case lDateFormat[lCount] of
      'Y':
      if lYearPos = 0 then
      begin
        Inc(lWhich);
        lYearPos := lWhich;
      end;
      'M':
      if lMonthPos = 0 then
      begin
        Inc(lWhich);
        lMonthPos := lWhich;
      end;
      'D':
      if lDayPos = 0 then
      begin
        Inc(lWhich);
        lDayPos := lWhich;
      end;
    end;
  end;
  // Writeln('YP: ',lYearPos,', MP: ',lMonthPos,', DP: ',lDayPos);

  for lCount := 1 to 3 do
    lValues[lCount] := 0;
  lS1 := '';
  lNumSep := 0;
  for lCount := 1 to lLen do
  begin
    if Pos(AString[lCount], cDigits) > 0 then
      lS1 := lS1 + AString[lCount];
     { space can be part of the shortdateformat, and is defaultly in slovak
       windows, therefor it shouldn't be taken as separator (unless so specified)
       and ignored }

    if (ASeparator <> ' ') and (AString[lCount] = ' ') then
      Continue;
    if (AString[lCount] = ASeparator) or ((lCount = lLen) and (Pos(AString[lCount], cDigits) > 0)) then
    begin
      Inc(lNumSep);
      if lNumSep > 3 then
      begin
//        Writeln('More than 3 separators');
        FixErrorMsg(AString);
        Exit;
      end;

         // Check if the year has more then two Digits (if lNumSep = lYearPos, then we are evaluating the year.)
      if (lNumSep = lYearPos) and (Length(lS1) > 2) then
        lYearMoreThenTwoDigits := True;
      Val(lS1, lValues[lNumSep], lCode);
      if lCode <> 0 then
      begin
//        Writeln('S1 not a number ',lS1);
        FixErrorMsg(AString);
        Exit;
      end;
      lS1 := '';
    end
    else if Pos(AString[lCount], cDigits) = 0 then
    begin
//      Writeln('Not a number at pos ',lCount,' ',AString[lCount]);
      FixErrorMsg(AString);
      Exit;
    end;
  end;
//  Writeln('Which : ',lWhich,' N : ',lNumSep);
  if (lWhich < 3) and (lNumSep > lWhich) then
  begin
    FixErrorMsg(AString);
    Exit;
  end;

  // Fill in lValues.
  DecodeDate(Date, lYear, lMonth, lDay);
  if lNumSep = 3 then
  begin
    lYearValues := lValues[lYearPos];
    lMonthValues := lValues[lMonthPos];
    lDayValues := lValues[lDayPos];
  end
  else
  begin
    lYearValues := lYear;
    if lNumSep < 2 then
    begin
      lDayValues := lValues[1];
      lMonthValues := lMonth;
    end
    else if lDayPos < lMonthPos then
    begin
      lDayValues := lValues[1];
      lMonthValues := lValues[2];
    end
    else
    begin
      lDayValues := lValues[2];
      lMonthValues := lValues[1];
    end;
  end;
  if (lYearValues >= 0) and (lYearValues < 100) and not lYearMoreThenTwoDigits then
  begin
    lYear := lYear - AFormatSettings.TwoDigitYearCenturyWindow;
    Inc(lYearValues, lYear div 100 * 100);
    if (TwoDigitYearCenturyWindow > 0) and (lYearValues < lYear) then
      Inc(lYearValues, 100);
  end;

  if not TryEncodeDate(lYearValues, lMonthValues, lDayValues, Result) then
    AErrorMsg := SErrInvalidDate;
end;

function TryStrToDate(const AString: string; out AValue: TDateTime; const AFormatSettings: TFormatSettings): Boolean; overload;
var
  lMsg: string;
begin
  Result := AString.NotIsEmpty;
  if Result then
  begin
    AValue := IntStrToDate(lMsg, AString, AFormatSettings.ShortDateFormat, AFormatSettings, #0);
    Result := lMsg.IsEmpty;
  end
  else
    AValue := 0;
end;

procedure UseVariable(const AValue: IInterface);
begin
end;

procedure UseVariable(const AValue: Integer);
begin
end;

procedure UseVariable(const AValue: string);
begin
end;

{ TBytesHelper }

function TBytesHelper.Compare(const ARight: TBytes): Integer;
var
  lCount: Integer;
begin
  if Length > ARight.Length then
    Exit(GreaterThanValue);

  if Length < ARight.Length then
    Exit(LessThanValue);

  Result := EqualsValue;
  for lCount := Low to High do
  begin
    if Self[lCount] > ARight[lCount] then
      Exit(GreaterThanValue);
    if Self[lCount] < ARight[lCount] then
      Exit(LessThanValue);
  end;
end;

function TBytesHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TBytesHelper.High: Integer;
begin
  Result := System.High(Self);
end;

function TBytesHelper.Low: Integer;
begin
  Result := System.Low(Self);
end;

procedure TBytesHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

function TBytesHelper.SubBytes(const AStartIndex: Integer): TBytes;
begin
  Result := System.Copy(Self, AStartIndex, Length);
end;

function TBytesHelper.SubBytes(const AStartIndex, ALength: Integer): TBytes;
begin
  Result := System.Copy(Self, AStartIndex, ALength);
end;

{ TWordsHelper }

class function TWordsHelper.FromBytes(const ABytes: TBytes): TWords;
const
  cBytesPerWord = 2;
var
  lCount: Integer;
  lLow: Byte;
  lHigh: Byte;
begin
  lCount := ABytes.Length;
  if Odd(lCount) then
    Inc(lCount);
  Result.Length := lCount div cBytesPerWord;

  lCount := ABytes.Low;
  while lCount <= ABytes.High do
  begin
    lLow := ABytes[lCount];
    if lCount + 1 <= ABytes.High then
      lHigh := ABytes[lCount + 1]
    else
      lHigh := 0;

    Result[lCount div cBytesPerWord] := Word.FromBytes(lLow, lHigh);
    Inc(lCount, cBytesPerWord);
  end;

end;

function TWordsHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TWordsHelper.High: Integer;
begin
  Result := System.High(Self);
end;

function TWordsHelper.Low: Integer;
begin
  Result := System.Low(Self);
end;

procedure TWordsHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

function TWordsHelper.ToBytes: TBytes;
const
  cBytesPerWord = 2;
var
  lLow: Byte;
  lHigh: Byte;
  lCount: Integer;
  lNewIndex: Integer;
begin
  Result.Length := Length * cBytesPerWord;
  for lCount := Low to High do
  begin
    Self[lCount].ToBytes(lLow, lHigh);
    lNewIndex := lCount * cBytesPerWord;
    Result[lNewIndex] := lLow;
    Result[lNewIndex + 1] := lHigh;
  end;
end;

{ TCharHelper }

function TCharHelper.ToOrd: Integer;
begin
  Result := Ord(Self);
end;

function TCharHelper.ToUpper: Char;
begin
  Result := ToUpper(Self);
end;

class function TCharHelper.ToUpper(const AValue: Char): Char; assembler;
asm
  return AValue.toUpperCase();
end;

{ TIntegerHelper }

function TIntegerHelper.Equals(const AValue: Integer): Boolean;
begin
  Result := Self = AValue;
end;

function TIntegerHelper.NotEquals(const AValue: Integer): Boolean;
begin
  Result := not Equals(AValue);
end;

class function TIntegerHelper.Parse(const AValue: string): Integer;
begin
  Result := StrToInt(AValue);
end;

function TIntegerHelper.ToString: string;
begin
  Result := ToString(Self);
end;

class function TIntegerHelper.ToString(const AValue: Integer): string; assembler;
asm
  return AValue.toString();
end;

class function TIntegerHelper.TryParse(const AString: string; out AValue: Integer): Boolean;
begin
  Result := TryStrToInt(AString, AValue);
end;

{ TLongIntHelper }

function TLongIntHelper.Equals(const AValue: LongInt): Boolean;
begin
  Result := Self = AValue;
end;

function TLongIntHelper.NotEquals(const AValue: LongInt): Boolean;
begin
  Result := not Equals(AValue);
end;

class function TLongIntHelper.Parse(const AValue: string): Integer;
begin
  Result := StrToInt(AValue);
end;

function TLongIntHelper.ToString: string;
begin
  Result := ToString(Self);
end;

class function TLongIntHelper.ToString(const AValue: LongInt): string;
asm
  return AValue.toString();
end;

class function TLongIntHelper.TryParse(const AString: string; out AValue: Integer): Boolean;
begin
  Result := TryStrToInt(AString, AValue);
end;

{ TWordHelper }

class function TWordHelper.FromBytes(const ALow, AHigh: Byte): Word;
const
  cMask: Byte = $FF;
  cBitPerByte = 8;
begin
  Result := ALow + (AHigh and (cMask shl cBitPerByte));
end;

procedure TWordHelper.ToBytes(out ALow, AHigh: Byte);
const
  cMask: Byte = $FF;
  cBitPerByte = 8;
begin
  ALow := Self and cMask;
  AHigh := Self and (cMask shl cBitPerByte);
end;

{ TStringHelper }

function TStringHelper.CompareTo(const AValue: string): Integer;
begin
  Result := CompareTo(Self, AValue);
end;

class function TStringHelper.CompareTo(const ALeft, ARight: string): Integer; assembler;
asm
  return ALeft.localeCompare(ARight);
end;

function TStringHelper.Contains(const AValue: string): Boolean;
begin
  Result := IndexOf(AValue) >= 0;
end;

function TStringHelper.Equals(const AValue: string): Boolean;
begin
  Result := Self = AValue;
end;

function TStringHelper.FirstChar: Char;
begin
  if NotIsEmpty then
    Result := Chars[0]
  else
    Result := #0;
end;

function TStringHelper.Format(const AArgs: array of JSValue): string;
begin
  Result := SysUtils.Format(Self, AArgs);
end;

class function TStringHelper.Format(const AValue: string; const AArgs: array of JSValue): string;
begin
  Result := SysUtils.Format(AValue, AArgs);
end;

function TStringHelper.get_Chars(const AIndex: Integer): Char;
begin
  Result := Self[AIndex + 1];
end;

function TStringHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TStringHelper.High: Integer;
begin
  Result := Length - 1;
end;

function TStringHelper.IndexOf(const AValue: Char): Integer;
begin
  Result := Pos(AValue, Self, 1) - 1;
end;

function TStringHelper.IndexOf(const AValue: string; const AStartIndex, ACount: Integer): Integer;
begin
  Result := Pos(AValue, Self, AStartIndex + 1) - 1;
  if (Result + AValue.Length) > (AStartIndex + ACount) then
    Result := -1;
end;

function TStringHelper.IndexOf(const AValue: string): Integer;
begin
  Result := System.Pos(AValue, Self) - 1;
end;

function TStringHelper.Insert(const AStartIndex: Integer; const AValue: string): string;
begin
  System.Insert(AValue, Self, AStartIndex + 1);
  Result := Self;
end;

function TStringHelper.IsEmpty: Boolean;
begin
  Result := Self = Empty;
end;

function TStringHelper.IsNumber: Boolean;
const
  cNumbers = ['0'..'9'];
var
  lCount: Integer;
begin
  if IsEmpty then
    Exit(False);

  for lCount := 0 to Length - 1 do
  begin
    if not (Chars[lCount] in cNumbers) then
      Exit(False);
  end;
  Result := True;
end;

function TStringHelper.LastIndexOf(const AValue: Char): Integer;
begin
  Result := Reverse.IndexOf(AValue);
  if Result >= 0 then
    Result := Length - Result - 1;
end;

class function TStringHelper.Low: Integer;
begin
  Result := 0;
end;

function TStringHelper.NotEquals(const AValue: string): Boolean;
begin
  Result := not Equals(AValue);
end;

function TStringHelper.NotIsEmpty: Boolean;
begin
  Result := Self <> Empty;
end;

function TStringHelper.PadLeft(const ATotalWidth: Integer): string;
const
  cDefPadChar = ' ';
begin
  Result := PadLeft(ATotalWidth, cDefPadChar);
end;

function TStringHelper.PadLeft(ATotalWidth: Integer; const APaddingChar: Char): string;
begin
  ATotalWidth := ATotalWidth - Length;
  if ATotalWidth > 0 then
    Result := System.StringOfChar(APaddingChar, ATotalWidth) + Self
  else
    Result := Self;
end;

function TStringHelper.Pos(const ASubStr, AStr: string; const AOffset: Integer): Integer;
asm
  return AStr.indexOf(ASubStr, AOffset - 1) + 1;
end;

function TStringHelper.Remove(const AStartIndex: Integer): string;
begin
  Result := Remove(AStartIndex, 1);
end;

function TStringHelper.Remove(const AStartIndex, ACount: Integer): string;
begin
  Result := SubString(0, AStartIndex - 1) + SubString(AStartIndex + ACount);
end;

function TStringHelper.Replace(const AOldValue, ANewValue: string): string;
begin
  Result := SysUtils.StringReplace(Self, AOldValue, ANewValue, [rfReplaceAll]);
end;

function TStringHelper.Reverse: string;
var
  lCount: Integer;
begin
  Result := Empty;
  Result.Length := Length;
  for lCount := 0 to Length - 1 do
    Result.Chars[Length - lCount - 1] := Self.Chars[lCount];
end;

procedure TStringHelper.set_Chars(const AIndex: Integer; const AValue: Char);
begin
  Self[AIndex + 1] := AValue;
end;

procedure TStringHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

function TStringHelper.Substring(const AStartIndex: Integer): string;
begin
  Result := System.Copy(Self, AStartIndex + 1, Self.Length);
end;

function TStringHelper.Substring(const AStartIndex, ALength: Integer): string;
begin
  Result := System.Copy(Self, AStartIndex + 1, ALength);
end;

function TStringHelper.ToLower: string;
var
  lValue: string;
begin
  lValue := Self;
  asm
    lValue = lValue.toLowerCase();
  end;
  Result := lValue;
end;

function TStringHelper.ToUpper: string;
var
  lValue: string;
begin
  lValue := Self;
  asm
    lValue = lValue.toUpperCase();
  end;
  Result := lValue;
end;

{ TDateTimeHelper }

function TDateTimeHelper.Equals(const AValue: TDateTime): Boolean;
begin
  Result := SameValue(Self, AValue);
end;

function TDateTimeHelper.NotEquals(const AValue: TDateTime): Boolean;
begin
  Result := not SameValue(Self, AValue);
end;

function TDateTimeHelper.ToString: string;
begin
  Result := DateTimeToStr(Self, FormatSettings);
end;

{ TFormatSettings }

class function TFormatSettings.Create: TFormatSettings;
begin
  Result := Create(GetBrowserLocale);
end;

class function TFormatSettings.Create(const ALocale: string): TFormatSettings;
const
  cDateSeps: array of Char = ['/', '.', '-'];
  cTimeSeps: array of Char = [':', '.', '-'];
  cDecSeps: array of Char = [',', '.'];
  cThousandSeps: array of Char = ['.', ','];
var
  lTimeStr: string;
  lCount: Integer;
begin
  Result.LongDayNames[1] := 'Sunday';
  Result.LongDayNames[2] := 'Monday';
  Result.LongDayNames[3] := 'Tuesday';
  Result.LongDayNames[4] := 'Wednesday';
  Result.LongDayNames[5] := 'Thursday';
  Result.LongDayNames[6] := 'Friday';
  Result.LongDayNames[7] := 'Saturday';
  Result.ShortDayNames[1] := 'Sun';
  Result.ShortDayNames[2] := 'Mon';
  Result.ShortDayNames[3] := 'Tue';
  Result.ShortDayNames[4] := 'Wed';
  Result.ShortDayNames[5] := 'Thu';
  Result.ShortDayNames[6] := 'Fri';
  Result.ShortDayNames[7] := 'Sat';
  Result.ShortMonthNames[1] := 'Jan';
  Result.ShortMonthNames[2] := 'Feb';
  Result.ShortMonthNames[3] := 'Mar';
  Result.ShortMonthNames[4] := 'Apr';
  Result.ShortMonthNames[5] := 'May';
  Result.ShortMonthNames[6] := 'Jun';
  Result.ShortMonthNames[7] := 'Jul';
  Result.ShortMonthNames[8] := 'Aug';
  Result.ShortMonthNames[9] := 'Sep';
  Result.ShortMonthNames[10] := 'Oct';
  Result.ShortMonthNames[11] := 'Nov';
  Result.ShortMonthNames[12] := 'Dec';
  Result.LongMonthNames[1] := 'January';
  Result.LongMonthNames[2] := 'February';
  Result.LongMonthNames[3] := 'March';
  Result.LongMonthNames[4] := 'April';
  Result.LongMonthNames[5] := 'May';
  Result.LongMonthNames[6] := 'June';
  Result.LongMonthNames[7] := 'July';
  Result.LongMonthNames[8] := 'August';
  Result.LongMonthNames[9] := 'September';
  Result.LongMonthNames[10] := 'October';
  Result.LongMonthNames[11] := 'November';
  Result.LongMonthNames[12] := 'December';
  Result.DateTimeToStrFormat[False] := 'c';
  Result.DateTimeToStrFormat[True] := 'f';
  Result.DateSeparator := '-';
  Result.TimeSeparator := ':';
  Result.ShortDateFormat := 'yyyy-mm-dd';
  Result.LongDateFormat := 'ddd, yyyy-mm-dd';
  Result.ShortTimeFormat := 'hh:nn';
  Result.LongTimeFormat := 'hh:nn:ss';
  Result.DecimalSeparator := '.';
  Result.ThousandSeparator := ',';
  Result.TimeAMString := 'AM';
  Result.TimePMString := 'PM';
  Result.TwoDigitYearCenturyWindow := 50;

  Result.ShortDateFormat := GetLocaleShortDateFormat(ALocale);

  for lCount := Low(cDateSeps) to High(cDateSeps) do
  begin
    if Result.ShortDateFormat.IndexOf(cDateSeps[lCount]) >= 0 then
      Result.DateSeparator := cDateSeps[lCount];
  end;

  asm
    var lEvent = new Date('Jan 1, 1980 06:07:08 GMT+00:00');
    var lLoc = "";
    if (ALocale == "")
      lLoc = navigator.language;
    else
      lLoc = ALocale;
    lTimeStr = lEvent.toLocaleTimeString(lLoc);
  end;

  for lCount := Low(cTimeSeps) to High(cTimeSeps) do
  begin
    if lTimeStr.IndexOf(cTimeSeps[lCount]) >= 0 then
      Result.TimeSeparator := cTimeSeps[lCount];
  end;

  for lCount := Low(Result.ShortDayNames) to High(Result.ShortDayNames) do
    Result.ShortDayNames[lCount] := GetLocaleShortDayName(lCount, ALocale);
  for lCount := Low(Result.LongDayNames) to High(Result.LongDayNames) do
    Result.LongDayNames[lCount] := GetLocaleLongDayName(lCount, ALocale);

  for lCount := Low(Result.ShortMonthNames) to High(Result.ShortMonthNames) do
    Result.ShortMonthNames[lCount] := GetLocaleShortMonthName(lCount, ALocale);
  for lCount := Low(Result.LongMonthNames) to High(Result.LongMonthNames) do
    Result.LongMonthNames[lCount] := GetLocaleLongMonthName(lCount, ALocale);

  Result.DecimalSeparator := GetLocaleDecimalSeparator(ALocale);

  for lCount := Low(cDecSeps) to High(cDecSeps) do
  begin
    if Result.DecimalSeparator = cDecSeps[lCount] then
    begin
      Result.ThousandSeparator := cThousandSeps[lCount];
      Break;
    end;
  end;
end;

class function TFormatSettings.GetBrowserLocale: string;
asm
  return navigator.language;
end;

class function TFormatSettings.GetLocaleDecimalSeparator(const ALocale: string): string; assembler;
asm
  var lNumber = 1.1;
  lNumber = lNumber.toLocaleString(ALocale).substring(1, 2);
  return lNumber;
end;

class function TFormatSettings.GetLocaleLongDayName(const ADayOfWeek: Integer; const ALocale: string): string; assembler;
asm
  var lBaseDate = new Date(2017, 0, 1); // Sunday
  lBaseDate.setDate(lBaseDate.getDate() + ADayOfWeek - 1);
  return lBaseDate.toLocaleDateString(ALocale, { weekday: 'long' });
end;

class function TFormatSettings.GetLocaleLongMonthName(const AMonth: Integer; const ALocale: string): string; assembler;
asm
  var lBaseDate = new Date(2017, AMonth - 1, 1);
  return lBaseDate.toLocaleDateString(ALocale, { AMonth: 'long' });
end;

class function TFormatSettings.GetLocaleShortDateFormat(ALocale: string): string;
const
  cDeli = '-';
  cShortLength = 2;
var
  lIndex: Integer;
begin
  Result := string.Empty;

  ALocale := ALocale.ToLower;

  lIndex := ALocale.IndexOf(cDeli);

  if lIndex >= 0 then
    // ensure the first part is lowercase, second part is uppercase
    ALocale := ALocale.SubString(0, lIndex) + ALocale.SubString(lIndex).ToUpper
  else
  begin
    // when the locale is the main locale specifier only without country
    if ALocale.Length = cShortLength then
      ALocale := ALocale + cDeli + ALocale.ToUpper;
  end;

  asm
    var lFormats = {
      "ar-SA" : "dd/MM/yy",
      "bg-BG" : "dd.M.yyyy",
      "ca-ES" : "dd/MM/yyyy",
      "zh-TW" : "yyyy/M/d",
      "cs-CZ" : "d.M.yyyy",
      "da-DK" : "dd-MM-yyyy",
      "de-DE" : "dd.MM.yyyy",
      "el-GR" : "d/M/yyyy",
      "en-US" : "M/d/yyyy",
      "fi-FI" : "d.M.yyyy",
      "fr-FR" : "dd/MM/yyyy",
      "he-IL" : "dd/MM/yyyy",
      "hu-HU" : "yyyy. MM. dd.",
      "is-IS" : "d.M.yyyy",
      "it-IT" : "dd/MM/yyyy",
      "ja-JP" : "yyyy/MM/dd",
      "ko-KR" : "yyyy-MM-dd",
      "nl-NL" : "d-M-yyyy",
      "nb-NO" : "dd.MM.yyyy",
      "pl-PL" : "yyyy-MM-dd",
      "pt-BR" : "d/M/yyyy",
      "ro-RO" : "dd.MM.yyyy",
      "ru-RU" : "dd.MM.yyyy",
      "hr-HR" : "d.M.yyyy",
      "sk-SK" : "d. M. yyyy",
      "sq-AL" : "yyyy-MM-dd",
      "sv-SE" : "yyyy-MM-dd",
      "th-TH" : "d/M/yyyy",
      "tr-TR" : "dd.MM.yyyy",
      "ur-PK" : "dd/MM/yyyy",
      "id-ID" : "dd/MM/yyyy",
      "uk-UA" : "dd.MM.yyyy",
      "be-BY" : "dd.MM.yyyy",
      "sl-SI" : "d.M.yyyy",
      "et-EE" : "d.MM.yyyy",
      "lv-LV" : "yyyy.MM.dd.",
      "lt-LT" : "yyyy.MM.dd",
      "fa-IR" : "MM/dd/yyyy",
      "vi-VN" : "dd/MM/yyyy",
      "hy-AM" : "dd.MM.yyyy",
      "az-Latn-AZ" : "dd.MM.yyyy",
      "eu-ES" : "yyyy/MM/dd",
      "mk-MK" : "dd.MM.yyyy",
      "af-ZA" : "yyyy/MM/dd",
      "ka-GE" : "dd.MM.yyyy",
      "fo-FO" : "dd-MM-yyyy",
      "hi-IN" : "dd-MM-yyyy",
      "ms-MY" : "dd/MM/yyyy",
      "kk-KZ" : "dd.MM.yyyy",
      "ky-KG" : "dd.MM.yy",
      "sw-KE" : "M/d/yyyy",
      "uz-Latn-UZ" : "dd/MM yyyy",
      "tt-RU" : "dd.MM.yyyy",
      "pa-IN" : "dd-MM-yy",
      "gu-IN" : "dd-MM-yy",
      "ta-IN" : "dd-MM-yyyy",
      "te-IN" : "dd-MM-yy",
      "kn-IN" : "dd-MM-yy",
      "mr-IN" : "dd-MM-yyyy",
      "sa-IN" : "dd-MM-yyyy",
      "mn-MN" : "yy.MM.dd",
      "gl-ES" : "dd/MM/yy",
      "kok-IN" : "dd-MM-yyyy",
      "syr-SY" : "dd/MM/yyyy",
      "dv-MV" : "dd/MM/yy",
      "ar-IQ" : "dd/MM/yyyy",
      "zh-CN" : "yyyy/M/d",
      "de-CH" : "dd.MM.yyyy",
      "en-GB" : "dd/MM/yyyy",
      "es-MX" : "dd/MM/yyyy",
      "fr-BE" : "d/MM/yyyy",
      "it-CH" : "dd.MM.yyyy",
      "nl-BE" : "d/MM/yyyy",
      "nn-NO" : "dd.MM.yyyy",
      "pt-PT" : "dd-MM-yyyy",
      "sr-Latn-CS" : "d.M.yyyy",
      "sv-FI" : "d.M.yyyy",
      "az-Cyrl-AZ" : "dd.MM.yyyy",
      "ms-BN" : "dd/MM/yyyy",
      "uz-Cyrl-UZ" : "dd.MM.yyyy",
      "ar-EG" : "dd/MM/yyyy",
      "zh-HK" : "d/M/yyyy",
      "de-AT" : "dd.MM.yyyy",
      "en-AU" : "d/MM/yyyy",
      "es-ES" : "dd/MM/yyyy",
      "fr-CA" : "yyyy-MM-dd",
      "sr-Cyrl-CS" : "d.M.yyyy",
      "ar-LY" : "dd/MM/yyyy",
      "zh-SG" : "d/M/yyyy",
      "de-LU" : "dd.MM.yyyy",
      "en-CA" : "dd/MM/yyyy",
      "es-GT" : "dd/MM/yyyy",
      "fr-CH" : "dd.MM.yyyy",
      "ar-DZ" : "dd-MM-yyyy",
      "zh-MO" : "d/M/yyyy",
      "de-LI" : "dd.MM.yyyy",
      "en-NZ" : "d/MM/yyyy",
      "es-CR" : "dd/MM/yyyy",
      "fr-LU" : "dd/MM/yyyy",
      "ar-MA" : "dd-MM-yyyy",
      "en-IE" : "dd/MM/yyyy",
      "es-PA" : "MM/dd/yyyy",
      "fr-MC" : "dd/MM/yyyy",
      "ar-TN" : "dd-MM-yyyy",
      "en-ZA" : "yyyy/MM/dd",
      "es-DO" : "dd/MM/yyyy",
      "ar-OM" : "dd/MM/yyyy",
      "en-JM" : "dd/MM/yyyy",
      "es-VE" : "dd/MM/yyyy",
      "ar-YE" : "dd/MM/yyyy",
      "en-029" : "MM/dd/yyyy",
      "es-CO" : "dd/MM/yyyy",
      "ar-SY" : "dd/MM/yyyy",
      "en-BZ" : "dd/MM/yyyy",
      "es-PE" : "dd/MM/yyyy",
      "ar-JO" : "dd/MM/yyyy",
      "en-TT" : "dd/MM/yyyy",
      "es-AR" : "dd/MM/yyyy",
      "ar-LB" : "dd/MM/yyyy",
      "en-ZW" : "M/d/yyyy",
      "es-EC" : "dd/MM/yyyy",
      "ar-KW" : "dd/MM/yyyy",
      "en-PH" : "M/d/yyyy",
      "es-CL" : "dd-MM-yyyy",
      "ar-AE" : "dd/MM/yyyy",
      "es-UY" : "dd/MM/yyyy",
      "ar-BH" : "dd/MM/yyyy",
      "es-PY" : "dd/MM/yyyy",
      "ar-QA" : "dd/MM/yyyy",
      "es-BO" : "dd/MM/yyyy",
      "es-SV" : "dd/MM/yyyy",
      "es-HN" : "dd/MM/yyyy",
      "es-NI" : "dd/MM/yyyy",
      "es-PR" : "dd/MM/yyyy",
      "am-ET" : "d/M/yyyy",
      "tzm-Latn-DZ" : "dd-MM-yyyy",
      "iu-Latn-CA" : "d/MM/yyyy",
      "sma-NO" : "dd.MM.yyyy",
      "mn-Mong-CN" : "yyyy/M/d",
      "gd-GB" : "dd/MM/yyyy",
      "en-MY" : "d/M/yyyy",
      "prs-AF" : "dd/MM/yy",
      "bn-BD" : "dd-MM-yy",
      "wo-SN" : "dd/MM/yyyy",
      "rw-RW" : "M/d/yyyy",
      "qut-GT" : "dd/MM/yyyy",
      "sah-RU" : "MM.dd.yyyy",
      "gsw-FR" : "dd/MM/yyyy",
      "co-FR" : "dd/MM/yyyy",
      "oc-FR" : "dd/MM/yyyy",
      "mi-NZ" : "dd/MM/yyyy",
      "ga-IE" : "dd/MM/yyyy",
      "se-SE" : "yyyy-MM-dd",
      "br-FR" : "dd/MM/yyyy",
      "smn-FI" : "d.M.yyyy",
      "moh-CA" : "M/d/yyyy",
      "arn-CL" : "dd-MM-yyyy",
      "ii-CN" : "yyyy/M/d",
      "dsb-DE" : "d. M. yyyy",
      "ig-NG" : "d/M/yyyy",
      "kl-GL" : "dd-MM-yyyy",
      "lb-LU" : "dd/MM/yyyy",
      "ba-RU" : "dd.MM.yy",
      "nso-ZA" : "yyyy/MM/dd",
      "quz-BO" : "dd/MM/yyyy",
      "yo-NG" : "d/M/yyyy",
      "ha-Latn-NG" : "d/M/yyyy",
      "fil-PH" : "M/d/yyyy",
      "ps-AF" : "dd/MM/yy",
      "fy-NL" : "d-M-yyyy",
      "ne-NP" : "M/d/yyyy",
      "se-NO" : "dd.MM.yyyy",
      "iu-Cans-CA" : "d/M/yyyy",
      "sr-Latn-RS" : "d.M.yyyy",
      "si-LK" : "yyyy-MM-dd",
      "sr-Cyrl-RS" : "d.M.yyyy",
      "lo-LA" : "dd/MM/yyyy",
      "km-KH" : "yyyy-MM-dd",
      "cy-GB" : "dd/MM/yyyy",
      "bo-CN" : "yyyy/M/d",
      "sms-FI" : "d.M.yyyy",
      "as-IN" : "dd-MM-yyyy",
      "ml-IN" : "dd-MM-yy",
      "en-IN" : "dd-MM-yyyy",
      "or-IN" : "dd-MM-yy",
      "bn-IN" : "dd-MM-yy",
      "tk-TM" : "dd.MM.yy",
      "bs-Latn-BA" : "d.M.yyyy",
      "mt-MT" : "dd/MM/yyyy",
      "sr-Cyrl-ME" : "d.M.yyyy",
      "se-FI" : "d.M.yyyy",
      "zu-ZA" : "yyyy/MM/dd",
      "xh-ZA" : "yyyy/MM/dd",
      "tn-ZA" : "yyyy/MM/dd",
      "hsb-DE" : "d. M. yyyy",
      "bs-Cyrl-BA" : "d.M.yyyy",
      "tg-Cyrl-TJ" : "dd.MM.yy",
      "sr-Latn-BA" : "d.M.yyyy",
      "smj-NO" : "dd.MM.yyyy",
      "rm-CH" : "dd/MM/yyyy",
      "smj-SE" : "yyyy-MM-dd",
      "quz-EC" : "dd/MM/yyyy",
      "quz-PE" : "dd/MM/yyyy",
      "hr-BA" : "d.M.yyyy.",
      "sr-Latn-ME" : "d.M.yyyy",
      "sma-SE" : "yyyy-MM-dd",
      "en-SG" : "d/M/yyyy",
      "ug-CN" : "yyyy-M-d",
      "sr-Cyrl-BA" : "d.M.yyyy",
      "es-US" : "M/d/yyyy"};
    return lFormats[ALocale] || 'dd/MM/yyyy';
  end;
end;

class function TFormatSettings.GetLocaleShortDayName(const ADayOfWeek: Integer; const ALocale: string): string; assembler;
asm
  var lBaseDate = new Date(2017, 0, 1); // Sunday
  lBaseDate.setDate(lBaseDate.getDate() + ADayOfWeek - 1);

  var lLoc = "";
  if (ALocale == '')
    lLoc = navigator.language
  else
    lLoc = ALocale;

  return lBaseDate.toLocaleDateString(lLoc, { weekday: 'short' });
end;

class function TFormatSettings.GetLocaleShortMonthName(const AMonth: Integer; const ALocale: string): string; assembler;
asm
  var lBaseDate = new Date(2017, AMonth - 1, 1);
  var lLoc = "";
  if (ALocale == '')
    lLoc = navigator.language
  else
    lLoc = ALocale;
  return lBaseDate.toLocaleDateString(lLoc, { AMonth: 'short' });
end;

{ TFormatDateTimeHelper }

class function TFormatDateTimeHelper.FormatDateTime(const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime): string;
var
  lYear: Word;
  lMonth: Word;
  lDay: Word;
  lDayOfWeek: Word;
  lHour: Word;
  lMinute: Word;
  lSecond: Word;
  lMilliSecond: Word;
begin
  Result := '';
  DecodeDateFully(ADateTime, lYear, lMonth, lDay, lDayOfWeek);
  DecodeTime(ADateTime, lHour, lMinute, lSecond, lMilliSecond);
  // Writeln(DateTime,'->',lYear,',', lMonth, ',',lDay, ',',lDayOfWeek,',',lHour, ',',lMinute, ',',lSecond, ',',lMilliSecond);
  if AFormatStr <> '' then
    StoreFormat(Result, lYear, lMonth, lDay, lHour, lMinute, lSecond, lMilliSecond, lDayOfWeek, AFormatStr, AFormatSettings, ADateTime, 0, False)
  else
    StoreFormat(Result, lYear, lMonth, lDay, lHour, lMinute, lSecond, lMilliSecond, lDayOfWeek, 'C', AFormatSettings, ADateTime, 0, False);
end;

class procedure TFormatDateTimeHelper.StoreFormat(var AResult: string; var AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek: Word; const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime; ANesting: Integer; ATimeFlag: Boolean);
const
  cAMPMFormat1 = 'A/P';
  cAMPMFormat2 = 'AMPM';
  cAMPMFormat3 = 'AM/PM';
var
  lToken: Char;
  lLastFormatToken: Char;
  lPrevLastToken: Char;
  lCount: Integer;
  lClock12: Boolean;
  lBuffer: Integer;
  lIsInterval: Boolean;
  lTemp: Integer;
  lFormatCurrent: Integer;
  lFormatEnd: Integer;
begin
  if ANesting > 1 then  // 0 is original string, 1 is included FormatString
    Exit;
  lFormatCurrent := 1;
  lFormatEnd := AFormatStr.Length;
  lClock12 := False;
  lIsInterval := False;
  // look for unquoted 12-hour clock lToken
  lTemp := 1;
  while lTemp <= lFormatEnd do
    begin
    lToken := AFormatStr[lTemp];
    case lToken of
      '''', '"':
      begin
        Inc(lTemp);
        while (lTemp < lFormatEnd) and (AFormatStr[lTemp] <> lToken) do
          Inc(lTemp);
      end;
      'A', 'a':
      begin
        if cAMPMFormat1.Equals(AFormatStr.Substring(lTemp - 1, cAMPMFormat1.Length))
          or cAMPMFormat2.Equals(AFormatStr.Substring(lTemp - 1, cAMPMFormat2.Length))
          or cAMPMFormat3.Equals(AFormatStr.Substring(lTemp - 1, cAMPMFormat3.Length))then
        begin
          lClock12 := True;
          Break;
        end;
      end;
    end;  // case
    Inc(lTemp);
  end ;
  lToken := #255;
  lLastFormatToken := ' ';
  lPrevLastToken := 'H';
  while lFormatCurrent <= lFormatEnd do
  begin
    lToken := AFormatStr[lFormatCurrent];
    lToken := lToken.ToUpper;
   //  Writeln('Treating token at pos ',lFormatCurrent,', : ',lToken,' (',AFormatStr,')');
    lCount := 1;
    lTemp := lFormatCurrent + 1;
    case lToken of
      '''', '"':
      begin
        while (lTemp < lFormatEnd) and (AFormatStr[lTemp] <> lToken) do
          Inc(lTemp);
        Inc(lTemp);
        lCount := lTemp - lFormatCurrent;
        StoreStr(AResult, AFormatStr, lFormatCurrent + 1, lCount - 2);
      end ;
      'A':
      begin
        if cAMPMFormat2.Equals(AFormatStr.Substring(lFormatCurrent - 1, cAMPMFormat2.Length)) then
        begin
          lCount := 4;
          if AHour < 12 then
            StoreString(AResult, AFormatSettings.TimeAMString)
          else
            StoreString(AResult, AFormatSettings.TimePMString);
        end
        else if cAMPMFormat3.Equals(AFormatStr.Substring(lFormatCurrent - 1, cAMPMFormat3.Length)) then
        begin
          lCount := 5;
          if AHour < 12 then
            StoreStr(AResult, AFormatStr, lFormatCurrent, 2)
          else
            StoreStr(AResult, AFormatStr, lFormatCurrent + 3, 2);
        end
        else if cAMPMFormat1.Equals(AFormatStr.Substring(lFormatCurrent - 1, cAMPMFormat1.Length)) then
        begin
          lCount := 3;
          if AHour < 12 then
            StoreStr(AResult, AFormatStr, lFormatCurrent, 1)
          else
            StoreStr(AResult, AFormatStr, lFormatCurrent + 2, 1);
        end
        else
          raise EConvertError.Create('Illegal character in format string');
      end ;
      '/':
      begin
        //  Writeln('Detected date separator');
        StoreString(AResult, AFormatSettings.DateSeparator);
      end;
      ':': StoreString(AResult, AFormatSettings.TimeSeparator);
      ' ', 'C', 'D', 'H', 'M', 'N', 'S', 'T', 'Y', 'Z', 'F' :
      begin
        // Writeln(lFormatCurrent,' Special Token: ',lToken,', Count: ',lCount,', P: ',lTemp);
        while (lTemp <= lFormatEnd) and (AFormatStr.Chars[lTemp - 1].ToUpper = lToken) do
          Inc(lTemp);
        lCount := lTemp - lFormatCurrent;
        // Writeln(lFormatCurrent,' Special Token: ',lToken,', Count: ',lCount,', P: ',lTemp);
        case lToken of
          ' ': StoreStr(AResult, AFormatStr, lFormatCurrent, lCount);
          'Y': begin
            if lCount > 2 then
              StoreInt(AResult, AYear, 4)
            else
              StoreInt(AResult, AYear mod 100, 2);
          end;
          'M': begin
      if lIsInterval and ((lPrevLastToken = 'H') or ATimeFlag) then
        StoreInt(AResult, AMinute + (AHour + Trunc(Abs(ADateTime))*24)*60, 0)
      else
            if (lLastFormatToken = 'H') or ATimeFlag then
            begin
              if lCount = 1 then
                StoreInt(AResult, AMinute, 0)
              else
                StoreInt(AResult, AMinute, 2);
            end
            else
            begin
              case lCount of
                1: StoreInt(AResult, AMonth, 0);
                2: StoreInt(AResult, AMonth, 2);
                3: StoreString(AResult, AFormatSettings.ShortMonthNames[AMonth]);
              else
                StoreString(AResult, AFormatSettings.LongMonthNames[AMonth]);
              end;
            end;
          end;
          'D': begin
            case lCount of
              1: StoreInt(AResult, ADay, 0);
              2: StoreInt(AResult, ADay, 2);
              3: StoreString(AResult, AFormatSettings.ShortDayNames[ADayOfWeek]);
              4: StoreString(AResult, AFormatSettings.LongDayNames[ADayOfWeek]);
              5: StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.ShortDateFormat, AFormatSettings, ADateTime, ANesting + 1, False);
            else
              StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.LongDateFormat, AFormatSettings, ADateTime, ANesting + 1, False);
            end ;
          end ;
          'H':
      if lIsInterval then
        StoreInt(AResult, AHour + Trunc(Abs(ADateTime))*24, 0)
      else
      if lClock12 then
            begin
              lBuffer := AHour mod 12;
              if lBuffer=0 then lBuffer:=12;
              if lCount = 1 then
                StoreInt(AResult, lBuffer, 0)
              else
                StoreInt(AResult, lBuffer, 2);
            end
            else begin
              if lCount = 1 then
                StoreInt(AResult, AHour, 0)
              else
                StoreInt(AResult, AHour, 2);
            end;
          'N': if lIsInterval then
           StoreInt(AResult, AMinute + (AHour + Trunc(Abs(ADateTime))*24)*60, 0)
   else
   if lCount = 1 then
                 StoreInt(AResult, AMinute, 0)
               else
                 StoreInt(AResult, AMinute, 2);
          'S': if lIsInterval then
           StoreInt(AResult, ASecond + (AMinute + (AHour + Trunc(Abs(ADateTime))*24)*60)*60, 0)
         else
         if lCount = 1 then
                 StoreInt(AResult, ASecond, 0)
               else
                 StoreInt(AResult, ASecond, 2);
          'Z': if lCount = 1 then
                 StoreInt(AResult, AMilliSecond, 0)
               else
                 StoreInt(AResult, AMilliSecond, 3);
          'T': if lCount = 1 then
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.ShortTimeFormat, AFormatSettings, ADateTime, ANesting + 1, True)
               else
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.LongTimeFormat, AFormatSettings, ADateTime, ANesting + 1, True);
          'C': begin
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.ShortDateFormat, AFormatSettings, ADateTime, ANesting + 1, False);
                 if (AHour <> 0) or (AMinute <> 0) or (ASecond <> 0) then
                   begin
                    StoreString(AResult, ' ');
                    StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.LongTimeFormat, AFormatSettings, ADateTime, ANesting + 1, True);
                   end;
               end;
          'F': begin
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.ShortDateFormat, AFormatSettings, ADateTime, ANesting + 1, False);
                 StoreString(AResult, ' ');
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.LongTimeFormat, AFormatSettings, ADateTime, ANesting + 1, True);
               end;
        end;
        lPrevLastToken := lLastFormatToken;
        lLastFormatToken := lToken;
      end;
      else
        StoreString(AResult, lToken);
    end;
    Inc(lFormatCurrent, lCount);
  end;
end;

class procedure TFormatDateTimeHelper.StoreInt(var AResult: string; const AValue, ADigits: Integer);
const
  cFillChar = '0';
var
  lBuffer: string;
begin
  lBuffer := AValue.ToString;
  while (lBuffer.Length < ADigits) do
    lBuffer := cFillChar + lBuffer;
  StoreString(AResult, lBuffer);
end;

class procedure TFormatDateTimeHelper.StoreStr(var AResult: string; const AFormatStr: string; const APos, ALength: Integer);
begin
  AResult := AResult + Copy(AFormatStr, APos, ALength);
end;

class procedure TFormatDateTimeHelper.StoreString(var AResult: string; const AString: string);
begin
  AResult := AResult + AString;
end;

{ TEncoding }

function TEncoding.GetByteCount(const AChars: string; const ACharCount: Integer): Integer;
begin
  Result := GetByteCount(AChars.Substring(0, ACharCount));
end;

function TEncoding.GetByteCount(const AChars: string; const ACharIndex, ACharCount: Integer): Integer;
begin
  Result := GetByteCount(AChars.Substring(ACharIndex, ACharCount));
end;

function TEncoding.GetBytes(const AChars: string; const ACharCount: Integer): TBytes;
begin
  Result := GetBytes(AChars.Substring(0, ACharCount));
end;

function TEncoding.GetBytes(const AChars: string; const ACharIndex, ACharCount: Integer): TBytes;
begin
  Result := GetBytes(AChars.Substring(ACharIndex, ACharCount));
end;

function TEncoding.GetString(const ABytes: TBytes; const ACharCount: Integer): string;
begin
  Result := GetString(ABytes.SubBytes(0, ACharCount));
end;

function TEncoding.GetString(const ABytes: TBytes; const ACharIndex, ACharCount: Integer): string;
begin
  Result := GetString(ABytes.SubBytes(ACharIndex, ACharCount));
end;

class function TEncoding.get_ANSI: TEncoding;
begin
  if FANSI = nil then
    FANSI := TANSIEncoding.Create;
  Result := FANSI;
end;

class function TEncoding.get_Default: TEncoding;
begin
  Result := ANSI;
end;

class function TEncoding.get_Unicode: TEncoding;
begin
  if FUnicode = nil then
    FUnicode := TUnicodeEncoding.Create;
  Result := FUnicode;
end;

class function TEncoding.IsStandardEncoding(AEncoding: TEncoding): Boolean;
begin
  Result := (AEncoding <> nil) and ((AEncoding = FANSI) or (AEncoding = FUnicode));
end;

{ TANSIEncoding }

function TANSIEncoding.GetByteCount(const AChars: string): Integer;
begin
  Result := AChars.Length;
end;

function TANSIEncoding.GetBytes(const AChars: string): TBytes;
begin
  Result := BytesOf(AChars);
end;

function TANSIEncoding.GetString(const ABytes: TBytes): string;
begin
  Result := StringOf(ABytes);
end;

{ TUnicodeEncoding }

function TUnicodeEncoding.GetByteCount(const AChars: string): Integer;
begin
  Result := Char.Size * AChars.Length;
end;

function TUnicodeEncoding.GetBytes(const AChars: string): TBytes;
begin
  InternalGetBytes(AChars, Result);
end;

function TUnicodeEncoding.GetString(const ABytes: TBytes): string;
var
  lWords: TWords;
  lBuffer: string;
begin
  lBuffer := string.Empty;
  lWords := TWords.FromBytes(ABytes);
  if lWords <> nil then
  begin
    asm
      for (var lCount = 0; lCount < lWords.length; lCount++)
        lBuffer += String.fromCharCode(lWords[lCount]);
    end;
  end;
  Result := lBuffer;
end;

procedure TUnicodeEncoding.InternalGetBytes(const AChars: string; out ABytes: TBytes);
var
  lWords: TWords;
begin
  lWords := nil;
  asm
    for (var lCount = 0; lCount < AChars.length; lCount++)
      lWords.push(AChars.charCodeAt(lCount));
  end;
  ABytes := lWords.ToBytes;
end;

{ TStream }

function TStream.CopyFrom(const ASource: TStream; ACount: Int64): Int64;
const
  MaxBufSize = $F000;
var
  lBufSize: Integer;
  lCount: Integer;
  lBuffer: TBytes;
begin
  if ACount <= 0 then
  begin
    ASource.Position := 0;
    ACount := ASource.Size;
  end;
  Result := ACount;
  if ACount > MaxBufSize then
    lBufSize := MaxBufSize
  else
    lBufSize := ACount;
  lBuffer.Length := lBufSize;
  try
    while ACount <> 0 do
    begin
      if ACount > lBufSize then
        lCount := lBufSize
      else
        lCount := ACount;
      ASource.ReadBuffer(lBuffer, lCount);
      WriteBuffer(lBuffer, lCount);
      Dec(ACount, lCount);
    end;
  finally
    lBuffer.Length := 0;
  end;
end;

function TStream.Read(var ABuffer: TBytes; const ACount: Longint): Longint;
begin
  Result := Read(ABuffer, 0, ACount);
end;

function TStream.Read(var ABuffer: TBytes; const AOffset, ACount: Longint): Longint;
begin
  Result := 0;
end;

procedure TStream.ReadBuffer(var ABuffer: TBytes; const ACount: NativeInt);
begin
  ReadBuffer(ABuffer, 0, ACount);
end;

procedure TStream.ReadBuffer(var Buffer: TBytes; const AOffset, ACount: NativeInt);
var
  lTotalCount,
  lReadCount: NativeInt;
begin
  lTotalCount := Read(Buffer, AOffset, ACount);
  if lTotalCount < 0 then
    raise EReadError.Create(SReadError);

  while (lTotalCount < ACount) do
  begin
    { Try to read a contiguous block of <ACount> size }
    lReadCount := Read(Buffer, AOffset + lTotalCount, (ACount - lTotalCount));

    { Check if we read something and decrease the number of bytes left to read }
    if lReadCount <= 0 then
      raise EReadError.Create(SReadError)
    else
      Inc(lTotalCount, lReadCount);
  end
end;

procedure TStream.set_Size(const ANewSize: Int64);
begin
end;

function TStream.Write(const ABuffer: TBytes; const ACount: Longint): Longint;
begin
  Result := 0;
end;

function TStream.Write(const ABuffer: TBytes; const AOffset, ACount: Longint): Longint;
var
  lBytes: TBytes;
  lCount: Integer;
begin
  Result := 0;
  if ABuffer.Length - AOffset > 0 then
  begin
    lBytes.Length := ABuffer.Length - AOffset;
    for lCount := AOffset to ABuffer.Length - 1 do
      lBytes[lCount - AOffset] := ABuffer[lCount];

    Result := Write(lBytes, ACount);
  end;
end;

procedure TStream.WriteBuffer(const ABuffer: TBytes; const ACount: NativeInt);
begin
  WriteBuffer(ABuffer, 0, ACount);
end;

procedure TStream.WriteBuffer(const Buffer: TBytes; Offset, Count: NativeInt);
var
  lTotalCount,
  lWrittenCount: NativeInt;
begin
  lTotalCount := Write(Buffer, Offset, Count);
  if lTotalCount < 0 then
    raise EWriteError.Create(SWriteError);

  while lTotalCount < Count do
  begin
    lWrittenCount := Write(Buffer, Offset + lTotalCount, (Count - lTotalCount));

    if lWrittenCount <= 0 then
      raise EWriteError.Create(SWriteError)
    else
      Inc(lTotalCount, lWrittenCount);
  end;
end;

{ TCustomMemoryStream }

procedure TCustomMemoryStream.Clear;
begin
  FBytes.Length := 0;
  FCapacity := 0;
  FPosition := 0;
end;

procedure TCustomMemoryStream.LoadFromStream(const AStream: TStream);
var
  lCount: Int64;
begin
  AStream.Position := 0;
  lCount := AStream.Size;
  Size := lCount;
  if lCount <> 0 then
    AStream.ReadBuffer(FBytes, lCount);
end;

function TCustomMemoryStream.Read(var ABuffer: TBytes; const AOffset, ACount: Longint): Longint;
var
  lCount: Integer;
begin
  if (FPosition >= 0) and (ACount >= 0) then
  begin
    if FSize > FPosition then
    begin
      if FSize > ACount + FPosition then
        Result := ACount
      else
        Result := FSize - FPosition;

      ABuffer.Length := Min(Size, ACount);

      for lCount := AOffset + FPosition to Min(Size - 1, AOffset + FPosition + ACount - 1) do
        ABuffer[lCount - AOffset - FPosition] := FBytes[lCount];

      Inc(FPosition, Result);
      Exit;
    end;
  end;
  Result := 0;
end;

procedure TCustomMemoryStream.SaveToStream(const AStream: TStream);
begin
  if FSize > 0 then
    AStream.WriteBuffer(FBytes, FSize);
end;

procedure TCustomMemoryStream.set_Capacity(const ANewCapacity: NativeInt);
begin
  FBytes.Length := ANewCapacity;
  FCapacity := ANewCapacity;
end;

procedure TCustomMemoryStream.set_Size(const ANewSize: Int64);
var
  lOldPosition: Longint;
begin
  lOldPosition := FPosition;
  Capacity := ANewSize;
  FSize := ANewSize;
  if lOldPosition > ANewSize then
    Position := ANewSize;
end;

function TCustomMemoryStream.Write(const ABuffer: TBytes; const ACount: Longint): Longint;
var
  lPos: Int64;
  lCount: Integer;
begin
  if (FPosition >= 0) and (ACount >= 0) then
  begin
    lPos := FPosition + ACount;
    if lPos > 0 then
    begin
      if lPos > FSize then
      begin
        if lPos > FCapacity then
          Capacity := lPos;
        FSize := lPos;
      end;

      for lCount := 0 to ABuffer.Length - 1 do
        FBytes[FPosition + lCount] := ABuffer[lCount];

      FPosition := lPos;
      Result := ACount;
      Exit;
    end;
  end;
  Result := 0;
end;

{ TStringStream }

constructor TStringStream.Create;
begin
  Create(string.Empty, TEncoding.Default, False);
end;

constructor TStringStream.Create(const AString: string);
begin
  Create(AString, TEncoding.Default, False);
end;

constructor TStringStream.Create(const AString: string; AEncoding: TEncoding; AOwnsEncoding: Boolean);
begin
  inherited Create;
  FEncoding := AEncoding;
  FOwnsEncoding := AOwnsEncoding and not TEncoding.IsStandardEncoding(AEncoding);
  FBytes := FEncoding.GetBytes(AString);
  FSize := FBytes.Length;
  FCapacity := FSize;
end;

destructor TStringStream.Destroy;
begin
  if FOwnsEncoding then
    FEncoding.Free;
  inherited Destroy;
end;

function TStringStream.GetDataString: string;
begin
  Result := FEncoding.GetString(FBytes);
end;

procedure TStringStream.LoadFromFile(const AFileName: string);
const
  cStatusOK = 200;
var
  lStatus: Integer;
  lResponse: string;
begin
  lResponse := string.Empty;
  lStatus := cStatusOK;
  asm
    var lRequest = new XMLHttpRequest();
    lRequest.open("GET", AFileName, false);
    lRequest.send();

    if (lRequest.status == cStatusOK)
      lResponse = lRequest.responseText
    else
      lStatus = lRequest.status;
  end;

  if lStatus <> cStatusOK then
    raise EStringStreamError.CreateFmt(SCannotGetFile, [AFileName, lStatus]);
  WriteString(lResponse);
end;

procedure TStringStream.LoadFromFile(const AFileName: string; const ALoaded: TProc);
begin
  LoadFromFile(AFileName, ALoaded, nil);
end;

procedure TStringStream.LoadFromFile(const AFileName: string; const ALoaded: TProc; const AError: TProcString);
const
  cAbortMethod = 'abort';
  cErrorMethod = 'error';
  cGetMethod = 'GET';
  cLoadMethod = 'load';
begin
  FLoaded := ALoaded;
  FError := AError;
  FFileName := AFileName;
  FRequest := TJSXMLHttpRequest.New;
  FRequest.AddEventListener(cAbortMethod, @OnAbort);
  FRequest.AddEventListener(cErrorMethod, @OnError);
  FRequest.AddEventListener(cLoadMethod, @OnLoad);
  try
    FRequest.Open(cGetMethod, AFileName, True);
    FRequest.Send;
  except
    on E: Exception do
    begin
      if Assigned(AError) then
        AError(E.Message)
      else
        raise;
    end;
  end;
end;

function TStringStream.OnAbort(AEvent: TEventListenerEvent): Boolean;
begin
  Result := True;
  if Assigned(FError) then
    FError(Format(SLoadFromFileAborted, [FFileName]));
end;

function TStringStream.OnError(AEvent: TEventListenerEvent): Boolean;
begin
  Result := True;
  if Assigned(FError) then
    FError(Format(SErrorLoadFromFile, [FFileName]));
end;

function TStringStream.OnLoad(AEvent: TEventListenerEvent): Boolean;
const
  cStatusOK = 200;
  cStatusLastOK = 208;
var
  lReq: TJSXMLHttpRequest;
begin
  lReq := TJSXMLHttpRequest(AEvent.target);

  if (lReq.Status < cStatusOK) or (lReq.Status > cStatusLastOK) then
  begin
    if Assigned(FError) then
      FError(Format(SCannotGetFile, [FFileName, FRequest.Status]))
    else
      raise EStringStreamError.CreateFmt(SCannotGetFile, [FFileName, lReq.Status]);
  end;
  WriteString(lReq.ResponseText);
  Result := True;
  if Assigned(FLoaded) then
    FLoaded();
end;

function TStringStream.ReadString(ACount: Integer): string;
var
  lBytes: TBytes;
  lCount: Integer;
begin
  if ACount > Size - Position then
    ACount := Size - Position;

  lBytes.Length := ACount;
  for lCount := Position to ACount + Position - 1 do
    lBytes[lCount - Position] := FBytes[lCount];

  Result := FEncoding.GetString(lBytes);
  Position := Position + ACount;
end;

procedure TStringStream.WriteString(const AString: string);
var
  lBytes: TBytes;
begin
  lBytes := FEncoding.GetBytes(AString);
  Write(lBytes, lBytes.Length);
end;

{ TInterfaceList }

constructor TInterfaceList.Create;
begin
  inherited Create;
  FList := TFPList.Create;
end;

destructor TInterfaceList.Destroy;
begin
  Clear;
  FList.Free;
  inherited Destroy;
end;

function TInterfaceList.Add(const AItem: IInterface): Integer;
begin
  Result := FList.Add(nil);
  Items[Result] := AItem;
end;

procedure TInterfaceList.Clear;
var
  lCount: Integer;
begin
  for lCount := 0 to FList.Count - 1 do
    Items[lCount] := nil;
  FList.Clear;
end;

procedure TInterfaceList.Delete(const AIndex: Integer);
begin
  Items[AIndex] := nil;
  FList.Delete(AIndex);
end;

procedure TInterfaceList.Exchange(const AIndex1, AIndex2: Integer);
begin
  FList.Exchange(AIndex1, AIndex2);
end;

function TInterfaceList.Expand: TInterfaceList;
begin
  FList.Expand;
  Result := Self;
end;

function TInterfaceList.First: IInterface;
begin
  Result := Items[0];
end;

function TInterfaceList.GetEnumerator: TInterfaceListEnumerator;
begin
  Result := TInterfaceListEnumerator.Create(Self);
end;

function TInterfaceList.get_Capacity: Integer;
begin
  Result := FList.Capacity;
end;

function TInterfaceList.get_Count: Integer;
begin
  Result := FList.Count;
end;

function TInterfaceList.get_Items(const AIndex: Integer): IInterface;
begin
  Result := IInterface(FList[AIndex]);
end;

function TInterfaceList.IndexOf(const AItem: IInterface): Integer;
begin
  Result := IndexOfItem(AItem, TDirection.FromBeginning);
end;

function TInterfaceList.IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
begin
  Result := FList.IndexOfItem(AItem, ADirection);
end;

procedure TInterfaceList.Insert(const AIndex: Integer; const AItem: IInterface);
begin
  FList.Insert(AIndex, nil);
  Items[AIndex] := AItem;
end;

function TInterfaceList.Last: IInterface;
begin
  Result := Items[Count - 1];
end;

procedure TInterfaceList.Lock;
begin
end;

function TInterfaceList.Remove(const AItem: IInterface): Integer;
begin
  Result := RemoveItem(AItem, TDirection.FromBeginning);
end;

function TInterfaceList.RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
begin
  Result := FList.IndexOfItem(AItem, ADirection);
  if Result > -1 then
    Delete(Result);
end;

procedure TInterfaceList.set_Capacity(const AValue: Integer);
begin
  FList.Capacity := AValue;
end;

procedure TInterfaceList.set_Count(const AValue: Integer);
begin
  FList.Count := AValue;
end;

procedure TInterfaceList.set_Items(const AIndex: Integer; const AItem: IInterface);
begin
  if FList[AIndex] <> nil then
    IInterface(FList[AIndex])._Release;
  FList[AIndex] := AItem;
  if AItem <> nil then
    AItem._AddRef;
end;

function InternalCompare(AItem1, AItem2: JSValue): Integer;
begin
  Result := FCompare(IInterface(AItem1), IInterface(AItem2));
end;

procedure TInterfaceList.Sort(const ACompare: TCompareItems);
begin
  FCompare := ACompare;
  try
    FList.Sort(@InternalCompare);
  finally
    FCompare := nil;
  end;
end;

procedure TInterfaceList.Unlock;
begin
end;

{ TInterfaceListEnumerator }

constructor TInterfaceListEnumerator.Create(const AInterfaceList: TInterfaceList);
begin
  inherited Create;
  FIndex := -1;
  FInterfaceList := AInterfaceList;
end;

function TInterfaceListEnumerator.get_Current: IInterface;
begin
  Result := FInterfaceList[FIndex];
end;

function TInterfaceListEnumerator.MoveNext: Boolean;
begin
  Result := FIndex < FInterfaceList.Count - 1;
  if Result then
    Inc(FIndex);
end;

{ TPath }

class function TPath.Combine(const APath1, APath2: string): string;
begin
  if APath1.NotIsEmpty and (APath1.Chars[APath1.Length - 1] <> PathDelim) then
    Result := APath1 + PathDelim + APath2
  else
    Result := APath1 + APath2;
end;

class function TPath.GetDirectoryName(const AFileName: string): string;
var
  lIndex: Integer;
begin
  lIndex := AFileName.LastIndexOf(PathDelim);
  if lIndex >= 0 then
    Result := AFileName.SubString(0, lIndex)
  else
    Result := AFileName;
end;

{ TDirectory }

class function TDirectory.GetCurrentDirectory: string;
begin
  Result := TPath.GetDirectoryName(window.document.location.href);
end;

{ TStringBuilder }

constructor TStringBuilder.Create(const AValue: string);
begin
  inherited Create;
  FData := AValue;
end;

function TStringBuilder.Append(const AValue: Integer): TStringBuilder;
begin
  Result := Append(AValue.ToString);
end;

function TStringBuilder.Append(const AValue: string): TStringBuilder;
begin
  FData := FData + AValue;
  Result := Self;
end;

function TStringBuilder.get_Length: Integer;
begin
  Result := FData.Length;
end;

function TStringBuilder.Insert(const AIndex, AValue: Integer): TStringBuilder;
begin
  Result := Insert(AIndex, AValue.ToString);
end;

function TStringBuilder.Insert(const AIndex: Integer; const AValue: string): TStringBuilder;
begin
  FData.Insert(AIndex, AValue);
  Result := Self;
end;

procedure TStringBuilder.set_Length(const AValue: Integer);
begin
  FData.Length := AValue;
end;

function TStringBuilder.ToString: string;
begin
  Result := FData;
end;


initialization
  FormatSettings := TFormatSettings.Create;

end.
